/**
 * Tools Page
 * ტულების გვერდი
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@aiia/portal-core';
import {
  useTools,
  useCreateTool,
  useUpdateTool,
  useEnableTool,
  useDisableTool,
  useDeleteTool,
  useTestTool,
} from '../hooks/useTools';
import {
  type UserTool,
  type CreateToolRequest,
  type ToolAuthMode,
  TOOL_CATEGORIES,
  TOOL_AUTH_MODES,
} from '../features/api/tools';
import { getApiErrorMessage } from '../utils/errorMessages';

export default function ToolsPage() {
  const { t } = useTranslation();
  const notification = useNotification();
  const { tools, isLoading, refetch } = useTools();
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();
  const enableTool = useEnableTool();
  const disableTool = useDisableTool();
  const deleteTool = useDeleteTool();
  const testTool = useTestTool();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTool, setEditingTool] = useState<UserTool | null>(null);
  const [showTestModal, setShowTestModal] = useState<UserTool | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<UserTool | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateToolRequest>({
    toolName: '',
    toolDescription: '',
    toolCategory: TOOL_CATEGORIES[0].value,
    webhookUrl: '',
    webhookMethod: 'POST',
    webhookTimeoutMs: 5000,
    webhookRetryCount: 2,
    authMode: 'API_KEY',
    enabled: true,
    requiresConfirmation: false,
    parametersSchema: {},
    requiredParams: [],
    requestTemplate: undefined,
    successMessageTemplate: '',
    errorMessageTemplate: '',
  });

  // Parameters editor state
  const [newParamName, setNewParamName] = useState('');
  const [newParamType, setNewParamType] = useState('string');
  const [newParamDesc, setNewParamDesc] = useState('');
  const [newParamRequired, setNewParamRequired] = useState(false);

  // Raw text state for JSON textarea (avoids double-escaping on re-render)
  const [requestTemplateText, setRequestTemplateText] = useState('');

  // Test modal state
  const [testParams, setTestParams] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; executionTimeMs: number } | null>(null);

  const resetForm = () => {
    setFormData({
      toolName: '',
      toolDescription: '',
      toolCategory: TOOL_CATEGORIES[0].value,
      webhookUrl: '',
      webhookMethod: 'POST',
      webhookTimeoutMs: 5000,
      webhookRetryCount: 2,
      authMode: 'API_KEY',
      enabled: true,
      requiresConfirmation: false,
      parametersSchema: {},
      requiredParams: [],
      requestTemplate: undefined,
      successMessageTemplate: '',
      errorMessageTemplate: '',
    });
    setRequestTemplateText('');
    setNewParamName('');
    setNewParamType('string');
    setNewParamDesc('');
    setNewParamRequired(false);
  };

  const handleOpenEdit = (tool: UserTool) => {
    setEditingTool(tool);
    setFormData({
      toolName: tool.toolName,
      toolDescription: tool.toolDescription,
      toolCategory: tool.toolCategory,
      webhookUrl: tool.webhookUrl || '',
      webhookMethod: tool.webhookMethod || 'POST',
      webhookHeaders: tool.webhookHeaders,
      webhookTimeoutMs: tool.webhookTimeoutMs ?? 5000,
      webhookRetryCount: tool.webhookRetryCount ?? 2,
      authMode: 'API_KEY',
      enabled: tool.enabled,
      requiresConfirmation: tool.requiresConfirmation,
      parametersSchema: tool.parametersSchema || {},
      requiredParams: tool.requiredParams || [],
      requestTemplate: tool.requestTemplate,
      responseMapping: tool.responseMapping,
      successMessageTemplate: tool.successMessageTemplate || '',
      errorMessageTemplate: tool.errorMessageTemplate || '',
    });
    setRequestTemplateText(
      tool.requestTemplate ? JSON.stringify(tool.requestTemplate, null, 2) : ''
    );
    setShowCreateModal(true);
  };

  const handleAddParameter = () => {
    if (!newParamName) return;
    setFormData(prev => ({
      ...prev,
      parametersSchema: {
        ...prev.parametersSchema,
        [newParamName]: { type: newParamType, description: newParamDesc },
      },
      requiredParams: newParamRequired
        ? [...(prev.requiredParams || []), newParamName]
        : prev.requiredParams,
    }));
    setNewParamName('');
    setNewParamType('string');
    setNewParamDesc('');
    setNewParamRequired(false);
  };

  const handleRemoveParameter = (paramName: string) => {
    setFormData(prev => {
      const { [paramName]: _, ...restParams } = prev.parametersSchema || {};
      return {
        ...prev,
        parametersSchema: restParams,
        requiredParams: (prev.requiredParams || []).filter(p => p !== paramName),
      };
    });
  };

  const handleSave = async () => {
    if (!formData.toolName || !formData.toolDescription) {
      notification.error(t('tools.nameDescRequired'));
      return;
    }

    // Parse requestTemplate from raw text
    let parsedRequestTemplate: Record<string, unknown> | undefined;
    if (requestTemplateText.trim()) {
      try {
        parsedRequestTemplate = JSON.parse(requestTemplateText);
      } catch {
        notification.error(t('tools.invalidJson'));
        return;
      }
    }

    try {
      const dataToSend = { ...formData, requestTemplate: parsedRequestTemplate };

      if (editingTool) {
        await updateTool.mutateAsync({ id: editingTool.id, data: dataToSend });
        notification.success(t('tools.toolUpdated'));
      } else {
        await createTool.mutateAsync(dataToSend);
        notification.success(t('tools.toolCreated'));
      }
      setShowCreateModal(false);
      setEditingTool(null);
      resetForm();
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleToggleEnabled = async (tool: UserTool) => {
    try {
      if (tool.enabled) {
        await disableTool.mutateAsync(tool.id);
        notification.success(t('tools.toolDisabled'));
      } else {
        await enableTool.mutateAsync(tool.id);
        notification.success(t('tools.toolEnabled'));
      }
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const confirmDelete = (tool: UserTool) => {
    setToolToDelete(tool);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!toolToDelete) return;
    try {
      await deleteTool.mutateAsync(toolToDelete.id);
      notification.success(t('tools.toolDeleted'));
      setDeleteModalOpen(false);
      setToolToDelete(null);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleOpenTest = (tool: UserTool) => {
    setShowTestModal(tool);
    const params: Record<string, string> = {};
    if (tool.parametersSchema) {
      Object.keys(tool.parametersSchema).forEach(key => {
        params[key] = '';
      });
    }
    setTestParams(params);
    setTestResult(null);
  };

  const handleRunTest = async () => {
    if (!showTestModal) return;

    try {
      const result = await testTool.mutateAsync({ id: showTestModal.id, parameters: testParams });
      setTestResult(result);
    } catch (error: unknown) {
      setTestResult({ success: false, message: getApiErrorMessage(error, t), executionTimeMs: 0 });
    }
  };

  const getCategoryBadge = (category: string) => {
    const categoryInfo = TOOL_CATEGORIES.find(c => c.value === category);
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
        {categoryInfo?.label || category}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tools.title')}</h1>
          <p className="text-gray-600">{t('tools.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingTool(null);
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('tools.createTool')}
        </button>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools?.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {t('tools.emptyMessage')}
          </div>
        ) : (
          tools?.map((tool) => (
            <div
              key={tool.id}
              className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                tool.enabled ? 'border-green-500' : 'border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{tool.toolName}</h3>
                  {getCategoryBadge(tool.toolCategory)}
                </div>
                <div className="flex items-center gap-1">
                  {tool.enabled ? (
                    <span className="w-2 h-2 bg-green-500 rounded-full" aria-hidden="true"></span>
                  ) : (
                    <span className="w-2 h-2 bg-gray-400 rounded-full" aria-hidden="true"></span>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {tool.toolDescription}
              </p>

              <div className="text-xs text-gray-500 mb-3 space-y-1">
                {tool.hasWebhookUrl && (
                  <div>{t('tools.webhook')}: {tool.webhookMethod || 'POST'}</div>
                )}
                {tool.parametersSchema && (
                  <div>
                    {t('tools.parameters')}: {Object.keys(tool.parametersSchema).length}
                  </div>
                )}
                {tool.secretKeys && tool.secretKeys.length > 0 && (
                  <div>{t('tools.secrets')}: {tool.secretKeys.length}</div>
                )}
                {tool.requiresConfirmation && (
                  <div className="text-yellow-600">{t('tools.requiresConfirmation')}</div>
                )}
              </div>

              <div className="flex justify-between items-center pt-3 border-t">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenEdit(tool)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleOpenTest(tool)}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    {t('common.test')}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleEnabled(tool)}
                    className={`text-sm ${
                      tool.enabled
                        ? 'text-yellow-600 hover:text-yellow-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {tool.enabled ? t('common.disable') : t('common.enable')}
                  </button>
                  <button
                    onClick={() => confirmDelete(tool)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingTool ? t('tools.editTool') : t('tools.createTool')}
              </h2>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('tools.toolName')}
                    </label>
                    <input
                      type="text"
                      value={formData.toolName}
                      onChange={(e) => setFormData(prev => ({ ...prev, toolName: e.target.value }))}
                      placeholder={t('tools.toolNamePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('tools.category')}
                    </label>
                    <select
                      value={formData.toolCategory}
                      onChange={(e) => setFormData(prev => ({ ...prev, toolCategory: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      {TOOL_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tools.descriptionLabel')}
                  </label>
                  <textarea
                    value={formData.toolDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, toolDescription: e.target.value }))}
                    placeholder={t('tools.descriptionPlaceholder')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Webhook Config */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">{t('tools.webhookConfig')}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('tools.webhookUrl')}
                        {editingTool?.hasWebhookUrl && (
                          <span className="text-xs text-yellow-600 ml-2">({t('tools.reenterToUpdate')})</span>
                        )}
                      </label>
                      <input
                        type="url"
                        value={formData.webhookUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, webhookUrl: e.target.value }))}
                        placeholder={editingTool?.hasWebhookUrl ? t('tools.leaveEmptyToKeep') : t('tools.webhookUrlPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('tools.method')}
                      </label>
                      <select
                        value={formData.webhookMethod}
                        onChange={(e) => setFormData(prev => ({ ...prev, webhookMethod: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('tools.authMode')}
                      </label>
                      <select
                        value={formData.authMode}
                        onChange={(e) => setFormData(prev => ({ ...prev, authMode: e.target.value as ToolAuthMode }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        {TOOL_AUTH_MODES.map(mode => (
                          <option key={mode.value} value={mode.value}>{mode.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('tools.timeout')}
                        </label>
                        <input
                          type="number"
                          value={formData.webhookTimeoutMs}
                          onChange={(e) => setFormData(prev => ({ ...prev, webhookTimeoutMs: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('tools.retries')}
                        </label>
                        <input
                          type="number"
                          value={formData.webhookRetryCount}
                          onChange={(e) => setFormData(prev => ({ ...prev, webhookRetryCount: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parameters */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">{t('tools.parameters')}</h3>

                  {/* Existing parameters */}
                  {formData.parametersSchema && Object.keys(formData.parametersSchema).length > 0 && (
                    <div className="mb-4 space-y-2">
                      {Object.entries(formData.parametersSchema).map(([name, def]) => (
                        <div key={name} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                          <div>
                            <span className="font-medium">{name}</span>
                            <span className="text-gray-500 text-sm ml-2">({def.type})</span>
                            {formData.requiredParams?.includes(name) && (
                              <span className="text-red-500 text-sm ml-1">*</span>
                            )}
                            {def.description && (
                              <p className="text-xs text-gray-500">{def.description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveParameter(name)}
                            className="text-red-500 hover:text-red-700"
                            aria-label={`Remove parameter ${name}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new parameter */}
                  <div className="grid grid-cols-12 gap-2">
                    <input
                      type="text"
                      value={newParamName}
                      onChange={(e) => setNewParamName(e.target.value)}
                      placeholder={t('tools.parameterName')}
                      className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={newParamType}
                      onChange={(e) => setNewParamType(e.target.value)}
                      className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                    </select>
                    <input
                      type="text"
                      value={newParamDesc}
                      onChange={(e) => setNewParamDesc(e.target.value)}
                      placeholder={t('tools.parameterDescription')}
                      className="col-span-4 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="col-span-2 flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={newParamRequired}
                        onChange={(e) => setNewParamRequired(e.target.checked)}
                        className="rounded"
                      />
                      {t('common.required')}
                    </label>
                    <button
                      onClick={handleAddParameter}
                      className="col-span-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Request Template & Messages */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">{t('tools.requestTemplate')}</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('tools.requestBodyTemplate')}
                    </label>
                    <textarea
                      value={requestTemplateText}
                      onChange={(e) => setRequestTemplateText(e.target.value)}
                      placeholder={'{\n  "firstName": "${firstName}",\n  "phone": "${phone}"\n}'}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('tools.paramSubstitution')}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('tools.successMessage')}
                      </label>
                      <textarea
                        value={formData.successMessageTemplate || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, successMessageTemplate: e.target.value }))}
                        placeholder={t('tools.successPlaceholder')}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('tools.errorMessage')}
                      </label>
                      <textarea
                        value={formData.errorMessageTemplate || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, errorMessageTemplate: e.target.value }))}
                        placeholder={t('tools.errorPlaceholder')}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div className="border-t pt-4">
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.enabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">{t('common.enabled')}</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.requiresConfirmation}
                        onChange={(e) => setFormData(prev => ({ ...prev, requiresConfirmation: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">{t('tools.requiresUserConfirmation')}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTool(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={createTool.isPending || updateTool.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createTool.isPending || updateTool.isPending ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('tools.deleteTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('tools.deleteConfirm', { name: toolToDelete?.toolName })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setToolToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteTool.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteTool.isPending ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('tools.testTitle', { name: showTestModal.toolName })}
            </h2>

            <div className="space-y-4">
              {showTestModal.parametersSchema && Object.keys(showTestModal.parametersSchema).length > 0 ? (
                Object.entries(showTestModal.parametersSchema).map(([name, def]) => (
                  <div key={name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {name}
                      {showTestModal.requiredParams?.includes(name) && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                      <span className="text-gray-400 ml-2">({def.type})</span>
                    </label>
                    <input
                      type={def.type === 'number' ? 'number' : 'text'}
                      value={testParams[name] || ''}
                      onChange={(e) => setTestParams(prev => ({ ...prev, [name]: e.target.value }))}
                      placeholder={def.description}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">{t('tools.noParamsRequired')}</p>
              )}

              {testResult && (
                <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <div className="font-medium">{testResult.success ? t('common.success') : t('common.failed')}</div>
                  <div className="text-sm">{testResult.message}</div>
                  <div className="text-xs mt-1">{t('tools.executionTime', { ms: testResult.executionTimeMs })}</div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTestModal(null);
                  setTestParams({});
                  setTestResult(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
              <button
                onClick={handleRunTest}
                disabled={testTool.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {testTool.isPending ? t('common.testing') : t('tools.runTest')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
